import { z } from 'zod'
import { idSchema, isoDateTimeSchema, optionalText } from './common.js'

export const APPOINTMENT_STATUSES = ['scheduled', 'arrived', 'no_show', 'cancelled'] as const
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number]
export const appointmentStatusSchema = z.enum(APPOINTMENT_STATUSES)

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  scheduled: 'Expected',
  arrived: 'Arrived',
  no_show: "Didn't come",
  cancelled: 'Cancelled',
}

const appointmentFields = {
  clientId: idSchema.nullable().default(null),
  customerName: z.string().trim().max(120).default(''),
  phone: z.string().trim().max(32).default(''),
  carId: idSchema.nullable().default(null),
  carText: z.string().trim().max(120).default(''),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format'),
  time: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm format')
    .nullable()
    .default(null),
  reason: optionalText(500),
  notes: optionalText(2000),
}

function checkRefinements(
  value: {
    clientId: string | null
    customerName: string
    phone: string
    carId: string | null
    carText: string
    date: string
    time: string | null
    reason: string | null
    notes: string | null
  },
  ctx: z.RefinementCtx,
) {
  if (!value.clientId && !value.customerName) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['customerName'],
      message: 'Say who is coming',
    })
  }
}

export const appointmentInputSchema = z.object(appointmentFields).superRefine(checkRefinements)
export type AppointmentInput = z.infer<typeof appointmentInputSchema>

export const appointmentStatusUpdateSchema = z.object({
  status: appointmentStatusSchema,
})
export type AppointmentStatusUpdate = z.infer<typeof appointmentStatusUpdateSchema>

export const appointmentSchema = z.object({
  ...appointmentFields,
  id: idSchema,
  status: appointmentStatusSchema,
  jobcardId: idSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type Appointment = z.infer<typeof appointmentSchema>

/**
 * What the API returns: all stored fields plus derived display fields
 * for labels and joined data.
 */
export interface AppointmentWithDetails extends Appointment {
  /** The client's display name when clientId is set, else customerName. */
  customerLabel: string
  /** The client's phone when clientId is set and appointment phone is blank, else the appointment's phone. */
  phoneLabel: string
  /** The linked car's label when carId is set, else carText, else empty string. */
  carLabel: string
  /** The linked car's plate, or null. */
  plate: string | null
  /** The jobcard number this appointment is linked to, or null. */
  jobcardNumber: string | null
}
